import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaEye,
  FaEyeSlash,
  FaUserAlt,
  FaLock,
  FaArrowRight,
  FaTimes,
  FaExclamationCircle,
} from "react-icons/fa";
import CONFIG from "./../../config.js";

function LabourLogin() {
  const [mobileNumber, setMobileNumber] = useState("");
  const [otpArray, setOtpArray] = useState(["", "", "", ""]);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [popup, setPopup] = useState({
    open: false,
    title: "",
    message: "",
    type: "error",
  });

  const inputRefs = useRef([]);
  const navigate = useNavigate();

  const otpPassword = otpArray.join("");

  const showPopup = (title, message, type = "error") => {
    setPopup({
      open: true,
      title,
      message,
      type,
    });
  };

  const closePopup = () => {
    setPopup({
      open: false,
      title: "",
      message: "",
      type: "error",
    });
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileNumber(value);
  };

  const handleOtpChange = (index, value) => {
    const cleanValue = value.replace(/\D/g, "");

    if (!cleanValue) {
      const updatedOtp = [...otpArray];
      updatedOtp[index] = "";
      setOtpArray(updatedOtp);
      return;
    }

    const digit = cleanValue.slice(-1);
    const updatedOtp = [...otpArray];
    updatedOtp[index] = digit;
    setOtpArray(updatedOtp);

    if (index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (otpArray[index]) {
        const updatedOtp = [...otpArray];
        updatedOtp[index] = "";
        setOtpArray(updatedOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);

    if (!pastedData) return;

    const updatedOtp = ["", "", "", ""];
    pastedData.split("").forEach((digit, index) => {
      updatedOtp[index] = digit;
    });

    setOtpArray(updatedOtp);

    const focusIndex = pastedData.length >= 4 ? 3 : pastedData.length;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (mobileNumber.length !== 10) {
      showPopup("Invalid Mobile Number", "Please enter a valid 10-digit mobile number.");
      return;
    }

    if (otpPassword.length !== 4) {
      showPopup("Invalid Password", "Please enter only 4 digit password.");
      return;
    }

    try {
      setIsLoading(true);

      const url = `${CONFIG.API_BASE_URL}/api/labour/labour-login/`;

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile_number: mobileNumber,
          otp_password: otpPassword,
        }),
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok) {
        sessionStorage.setItem("isLoggedIn", "true");
        sessionStorage.setItem("labour_id", data.labour_id || "");
        sessionStorage.setItem("labourName", data.labour_name || "");
        sessionStorage.setItem("mobile", data.mobile_number || "");

        navigate("/labour/labour-dashboard", {
          state: {
            labour_name: data.labour_name || "",
            labour_id: data.labour_id || "",
            mobile_number: data.mobile_number || "",
            user_credits_consumed: data.user_credits_consumed || 0,
            referred_transaction_credits_consumed:
              data.referred_transaction_credits_consumed || 0,
            Total_credit_Point: data.Total_credit_Point || 0,
            orders_required_field: data.orders_required_field || [],
          },
        });
      } else {
        const errorMessage =
          data.message ||
          data.error ||
          data.detail ||
          "Invalid login or not registered. Please register first.";

        showPopup("Login Failed", errorMessage, "error");
      }
    } catch (error) {
      console.error("Error:", error);
      showPopup(
        "Server Error",
        "Something went wrong during login. Please try again.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    page: {
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background:
        "radial-gradient(circle at top left, #3b82f6 0%, #1d4ed8 35%, #0f172a 100%)",
      padding: "24px",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      position: "relative",
    },
    card: {
      width: "100%",
      maxWidth: "440px",
      background: "rgba(255,255,255,0.96)",
      borderRadius: "24px",
      padding: "34px 28px 26px",
      boxShadow: "0 22px 60px rgba(15, 23, 42, 0.28)",
      border: "1px solid rgba(255,255,255,0.45)",
      backdropFilter: "blur(10px)",
    },
    headerWrap: {
      textAlign: "center",
      marginBottom: "24px",
    },
    badge: {
      display: "inline-block",
      padding: "6px 12px",
      borderRadius: "999px",
      background: "#dbeafe",
      color: "#1d4ed8",
      fontSize: "12px",
      fontWeight: "700",
      marginBottom: "14px",
    },
    heading: {
      margin: "0 0 8px 0",
      fontSize: "2rem",
      fontWeight: "800",
      color: "#0f172a",
    },
    subText: {
      margin: 0,
      fontSize: "14px",
      color: "#64748b",
      lineHeight: "1.6",
    },
    note: {
      marginBottom: "22px",
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "14px",
      padding: "12px 14px",
      color: "#475569",
      fontSize: "13px",
      lineHeight: "1.6",
      textAlign: "center",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "10px",
      fontSize: "14px",
      fontWeight: "700",
      color: "#334155",
    },
    input: {
      width: "100%",
      height: "56px",
      padding: "0 16px",
      borderRadius: "14px",
      border: "1px solid #cbd5e1",
      background: "#f8fafc",
      fontSize: "16px",
      color: "#0f172a",
      outline: "none",
      boxSizing: "border-box",
    },
    otpSection: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    otpRow: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    otpWrapper: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "10px",
      flex: 1,
    },
    otpBox: {
      height: "58px",
      width: "100%",
      textAlign: "center",
      fontSize: "22px",
      fontWeight: "800",
      borderRadius: "14px",
      border: "1px solid #cbd5e1",
      background: "#f8fafc",
      color: "#0f172a",
      outline: "none",
      boxSizing: "border-box",
    },
    eyeBtn: {
      width: "56px",
      height: "56px",
      minWidth: "56px",
      borderRadius: "14px",
      border: "1px solid #cbd5e1",
      background: "#f8fafc",
      color: "#334155",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "18px",
    },
    helperRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    helper: {
      fontSize: "12px",
      color: "#64748b",
      margin: 0,
    },
    helperStatus: {
      fontSize: "12px",
      fontWeight: "700",
      color: showPassword ? "#16a34a" : "#64748b",
      margin: 0,
    },
    button: {
      width: "100%",
      height: "58px",
      border: "none",
      borderRadius: "16px",
      background: isLoading
        ? "#93c5fd"
        : "linear-gradient(135deg, #2563eb, #1d4ed8)",
      color: "#fff",
      fontSize: "17px",
      fontWeight: "800",
      cursor: isLoading ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      boxShadow: "0 14px 28px rgba(37, 99, 235, 0.28)",
      marginTop: "6px",
    },
    footerText: {
      textAlign: "center",
      marginTop: "20px",
      marginBottom: 0,
      fontSize: "14px",
      color: "#475569",
    },
    link: {
      color: "#2563eb",
      fontWeight: "700",
      textDecoration: "none",
    },

    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      zIndex: 9999,
    },
    modalBox: {
      width: "100%",
      maxWidth: "380px",
      background: "#fff",
      borderRadius: "22px",
      padding: "24px 22px 20px",
      boxShadow: "0 20px 50px rgba(0,0,0,0.22)",
      position: "relative",
      textAlign: "center",
    },
    modalClose: {
      position: "absolute",
      top: "12px",
      right: "12px",
      width: "36px",
      height: "36px",
      border: "none",
      borderRadius: "50%",
      background: "#f1f5f9",
      color: "#334155",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "14px",
    },
    modalIcon: {
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      margin: "0 auto 14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#fee2e2",
      color: "#dc2626",
      fontSize: "28px",
    },
    modalTitle: {
      margin: "0 0 8px 0",
      fontSize: "1.3rem",
      fontWeight: "800",
      color: "#0f172a",
    },
    modalMessage: {
      margin: "0 0 18px 0",
      fontSize: "14px",
      color: "#475569",
      lineHeight: "1.7",
    },
    modalButton: {
      width: "100%",
      height: "48px",
      border: "none",
      borderRadius: "14px",
      background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
      color: "#fff",
      fontSize: "15px",
      fontWeight: "700",
      cursor: "pointer",
    },
  };

  return (
    <>
      <div style={styles.page}>
        <form onSubmit={handleLogin} style={styles.card}>
          <div style={styles.headerWrap}>
            <span style={styles.badge}>Secure Access</span>
            <h2 style={styles.heading}>Labour Login</h2>
            <p style={styles.subText}>
              Sign in with your registered mobile number and 4-digit password.
            </p>
          </div>

          <div style={styles.note}>
            Please register first if you do not have an account. Enter only your
            4-digit password to continue.
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              <FaUserAlt />
              Mobile Number
            </label>
            <input
              type="text"
              value={mobileNumber}
              onChange={handleMobileChange}
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              <FaLock />
              4 Digit OTP Password
            </label>

            <div style={styles.otpSection}>
              <div style={styles.otpRow}>
                <div style={styles.otpWrapper}>
                  {otpArray.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type={showPassword ? "text" : "password"}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={handleOtpPaste}
                      required
                      style={styles.otpBox}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  style={styles.eyeBtn}
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <div style={styles.helperRow}>
                <p style={styles.helper}>Enter one digit in each box.</p>
                <p style={styles.helperStatus}>
                  {showPassword ? "Visible" : "Hidden"}
                </p>
              </div>
            </div>
          </div>

          <button type="submit" style={styles.button} disabled={isLoading}>
            {isLoading ? "Logging in..." : "Login"}
            {!isLoading && <FaArrowRight />}
          </button>

          <p style={styles.footerText}>
            Don&apos;t have an account?{" "}
            <Link to="/labour/labour-signup" style={styles.link}>
              Sign up here
            </Link>
          </p>
        </form>
      </div>

      {popup.open && (
        <div style={styles.modalOverlay} onClick={closePopup}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              style={styles.modalClose}
              onClick={closePopup}
            >
              <FaTimes />
            </button>

            <div style={styles.modalIcon}>
              <FaExclamationCircle />
            </div>

            <h3 style={styles.modalTitle}>{popup.title}</h3>
            <p style={styles.modalMessage}>{popup.message}</p>

            <button type="button" style={styles.modalButton} onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default LabourLogin;